import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PlanningBoard from '../components/PlanningBoard';
import PlanningTimeline from '../components/PlanningTimeline';
import WorkloadGrid from '../components/WorkloadGrid';
import TaskModal from '../modals/TaskModal';
import { useConfirm, useToast } from '../context/UIContext';
import { getProjectEntries } from '../services/ProjectServices';
import { getAllUsers } from '../services/UserService';
import { getWorkload } from '../services/PlanningService';
import {
    createTicket,
    deleteTicket,
    getTicketsByProject,
    updateTicketPlanning,
} from '../services/ProjectTicketService';
import { extractApiError } from '../utils/apiError';
import { addDays, projectTotals, toIso } from '../utils/planning';
import '../styles/Planning.css';

const TABS = [
    { id: 'board', label: 'Board' },
    { id: 'timeline', label: 'Zeitplan' },
    { id: 'workload', label: 'Auslastung' },
];

const fmtHours = (hours) => {
    const value = Number(hours) || 0;
    return Number.isInteger(value) ? `${value} h` : `${value.toFixed(1).replace('.', ',')} h`;
};

/** The workload range starts on a week boundary; the backend buckets by ISO week. */
const thisMonday = () => {
    const today = new Date();
    return addDays(today, -(today.getDay() === 0 ? 6 : today.getDay() - 1));
};

export default function Planning() {
    const toast = useToast();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const location = useLocation();

    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [people, setPeople] = useState([]);
    const [workload, setWorkload] = useState(null);
    const [tab, setTab] = useState('board');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [weeks, setWeeks] = useState(8);

    const urlProject = new URLSearchParams(location.search).get('project');

    useEffect(() => {
        let alive = true;
        Promise.all([getProjectEntries(), getAllUsers()])
            .then(([projectList, userList]) => {
                if (!alive) return;
                setProjects(projectList || []);
                setPeople((userList || []).filter(u => u.is_active !== false));
                const wanted = (projectList || []).find(p => String(p.id) === String(urlProject));
                setSelectedProject(wanted || (projectList || [])[0] || null);
            })
            .catch(e => toast(extractApiError(e, 'Projekte konnten nicht geladen werden.')))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // Read once on arrival: picking another project afterwards must not snap back.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadTasks = useCallback(async (projectId) => {
        if (!projectId) {
            setTasks([]);
            return;
        }
        try {
            setTasks(await getTicketsByProject(projectId) || []);
        } catch (e) {
            toast(extractApiError(e, 'Aufgaben konnten nicht geladen werden.'));
        }
    }, [toast]);

    useEffect(() => {
        loadTasks(selectedProject?.id);
    }, [selectedProject, loadTasks]);

    const loadWorkload = useCallback(async () => {
        const start = thisMonday();
        try {
            setWorkload(await getWorkload({
                dateFrom: toIso(start),
                dateTo: toIso(addDays(start, weeks * 7 - 1)),
            }));
        } catch (e) {
            toast(extractApiError(e, 'Auslastung konnte nicht geladen werden.'));
        }
    }, [weeks, toast]);

    useEffect(() => {
        if (tab === 'workload') loadWorkload();
    }, [tab, loadWorkload]);

    const pickProject = (project) => {
        setSelectedProject(project);
        navigate(`/planning?project=${project.id}`, { replace: true });
    };

    const moveTask = async (task, status) => {
        const previous = tasks;
        setTasks(cur => cur.map(t => (t.id === task.id ? { ...t, status } : t)));
        setBusy(true);
        try {
            await updateTicketPlanning(task.id, { status });
            await loadTasks(selectedProject?.id);
        } catch (e) {
            setTasks(previous);
            toast(extractApiError(e, 'Status konnte nicht geändert werden.'));
        } finally {
            setBusy(false);
        }
    };

    const rescheduleTask = async (task, dates) => {
        const previous = tasks;
        setTasks(cur => cur.map(t => (t.id === task.id ? { ...t, ...dates } : t)));
        setBusy(true);
        try {
            await updateTicketPlanning(task.id, dates);
            await loadTasks(selectedProject?.id);
        } catch (e) {
            setTasks(previous);
            toast(extractApiError(e, 'Termin konnte nicht geändert werden.'));
        } finally {
            setBusy(false);
        }
    };

    const saveTask = async (payload) => {
        try {
            if (editing) {
                await updateTicketPlanning(editing.id, payload);
                toast('Aufgabe gespeichert.');
            } else {
                await createTicket({ project_id: selectedProject.id, ...payload });
                toast('Aufgabe angelegt.');
            }
            setEditing(null);
            setCreating(false);
            await loadTasks(selectedProject?.id);
            if (tab === 'workload') await loadWorkload();
        } catch (e) {
            toast(extractApiError(e, 'Aufgabe konnte nicht gespeichert werden.'));
        }
    };

    const removeTask = async (task) => {
        const ok = await confirm(
            `Aufgabe „${task.title || 'Ohne Titel'}“ löschen? Bereits gebuchte Zeiten bleiben erhalten.`,
            { confirmLabel: 'Löschen', danger: true },
        );
        if (!ok) return;
        try {
            await deleteTicket(task.id);
            setEditing(null);
            await loadTasks(selectedProject?.id);
            toast('Aufgabe gelöscht.');
        } catch (e) {
            toast(extractApiError(e, 'Aufgabe konnte nicht gelöscht werden.'));
        }
    };

    const totals = useMemo(() => projectTotals(tasks), [tasks]);

    if (loading) return <div className="pl-page"><p className="pl-empty">Wird geladen…</p></div>;

    return (
        <div className="pl-page">
            <header className="pl-head">
                <div>
                    <h2>Projektplanung</h2>
                    <p className="pl-sub">
                        Aufgaben, Termine und Auslastung — auf denselben Tickets, auf die auch
                        Zeit gebucht wird.
                    </p>
                </div>
                {selectedProject && tab !== 'workload' && (
                    <button
                        type="button"
                        className="pl-btn pl-btn--primary"
                        onClick={() => setCreating(true)}
                    >
                        + Neue Aufgabe
                    </button>
                )}
            </header>

            {tab !== 'workload' && (
                <div className="pl-toolbar">
                    <label className="pl-field">
                        Projekt
                        <select
                            className="pl-input"
                            value={selectedProject?.id || ''}
                            onChange={e => pickProject(projects.find(p => String(p.id) === e.target.value))}
                        >
                            {projects.map(project => (
                                <option key={project.id} value={project.id}>{project.name}</option>
                            ))}
                        </select>
                    </label>

                    {selectedProject && (
                        <div className="pl-totals">
                            <span><strong>{totals.count}</strong> Aufgaben</span>
                            <span><strong>{fmtHours(totals.planned)}</strong> geplant</span>
                            <span><strong>{fmtHours(totals.open)}</strong> offen</span>
                            {totals.unscheduled > 0 && (
                                <span className="pl-warn">{totals.unscheduled} ohne Termin</span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {tab === 'workload' && (
                <div className="pl-toolbar">
                    <label className="pl-field">
                        Zeitraum
                        <select
                            className="pl-input"
                            value={weeks}
                            onChange={e => setWeeks(Number(e.target.value))}
                        >
                            <option value={4}>4 Wochen</option>
                            <option value={8}>8 Wochen</option>
                            <option value={12}>12 Wochen</option>
                            <option value={26}>26 Wochen</option>
                        </select>
                    </label>
                </div>
            )}

            <nav className="pl-tabs" role="tablist">
                {TABS.map(entry => (
                    <button
                        key={entry.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === entry.id}
                        className={`pl-tab${tab === entry.id ? ' pl-tab--active' : ''}`}
                        onClick={() => setTab(entry.id)}
                    >
                        {entry.label}
                    </button>
                ))}
            </nav>

            {!projects.length && (
                <p className="pl-empty">
                    Noch keine Projekte. Lege zuerst ein Projekt an, dann kannst du es hier planen.
                </p>
            )}

            {tab === 'board' && selectedProject && (
                <PlanningBoard
                    tasks={tasks}
                    busy={busy}
                    onMove={moveTask}
                    onCardClick={setEditing}
                />
            )}

            {tab === 'timeline' && selectedProject && (
                <PlanningTimeline
                    tasks={tasks}
                    onTaskClick={setEditing}
                    onReschedule={rescheduleTask}
                />
            )}

            {tab === 'workload' && (
                workload
                    ? <WorkloadGrid workload={workload} />
                    : <p className="pl-empty">Auslastung wird berechnet…</p>
            )}

            {(creating || editing) && (
                <TaskModal
                    task={editing}
                    projectId={selectedProject?.id}
                    people={people}
                    tasks={tasks}
                    onClose={() => { setEditing(null); setCreating(false); }}
                    onSaved={saveTask}
                    onDelete={editing ? () => removeTask(editing) : null}
                />
            )}
        </div>
    );
}
